import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { LogoBar } from "./LogoBar";
import { TrackingCard } from "./TrackingCard";
import { MailRookCard } from "./MailRookCard";

const FIELDS = [
  { k: "name", label: "Project name", ph: "e.g. Nordic Expansion" },
  { k: "from_name", label: "From name", ph: "e.g. Redoc Sales Team" },
  { k: "from_email", label: "From email (verified domain in Resend)", ph: "sales@yourdomain.com" },
  { k: "reply_to", label: "Reply-to email (optional)", ph: "replies@yourdomain.com" },
  { k: "footer_text", label: "Email footer text (optional)", ph: "Company name, address…" },
];

function DomainList() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/resend/domains").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="text-xs text-muted-foreground">Checking Resend…</p>;
  if (!d.connected) return <p className="text-xs text-rose-700" data-testid="resend-domains-error">Could not reach Resend: {d.error}</p>;
  if (d.restricted) return <p className="text-xs text-emerald-700" data-testid="resend-domains-restricted">Connected with a sending-only API key. Domain status can't be listed with this key. Check it at resend.com/domains.</p>;
  if (!d.domains.length) return <p className="text-xs text-amber-700" data-testid="resend-domains-empty">No domains in your Resend account yet. Add and verify one at resend.com/domains.</p>;
  return (
    <ul className="space-y-2" data-testid="resend-domains-list">
      {d.domains.map((x) => (
        <li key={x.name} className="flex justify-between text-sm font-mono">
          <span>{x.name}</span>
          <span className={x.status === "verified" ? "text-emerald-700" : "text-amber-700"}>{x.status}</span>
        </li>
      ))}
    </ul>
  );
}

export function SettingsPanel({ project, onSaved }) {
  const [f, setF] = useState(project);
  useEffect(() => setF(project), [project]);

  const save = async () => {
    try {
      await api.put(`/projects/${project.slot}`, {
        name: f.name, from_name: f.from_name || "", from_email: f.from_email || "", reply_to: f.reply_to || "", footer_text: f.footer_text || "",
      });
      toast.success("Project settings saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-6" data-testid="settings-panel">
      <section className="rounded-xl border border-border bg-card p-6 space-y-5">
        <div className="space-y-1.5"><Label>Company logo (used by {"{logo}"} in templates)</Label>
          <LogoBar slot={project.slot} project={project} onChange={onSaved} /></div>
        {FIELDS.map((x) => (
          <div key={x.k} className="space-y-1.5">
            <Label>{x.label}</Label>
            <Input value={f[x.k] || ""} placeholder={x.ph} onChange={(e) => setF({ ...f, [x.k]: e.target.value })} data-testid={`project-${x.k.replace("_", "-")}-input`} />
          </div>
        ))}
        <Button onClick={save} disabled={!f.name} data-testid="project-settings-save-button"><Save className="h-4 w-4 mr-1.5" />Save settings</Button>
      </section>
      <aside className="space-y-6 h-fit">
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <h3 className="font-display font-semibold">Resend sending domains</h3>
        <p className="text-xs text-muted-foreground leading-relaxed">All five projects share one Resend account. The From email must use one of your verified domains.</p>
        <DomainList />
      </div>
      <TrackingCard />
      <MailRookCard />
      </aside>
    </div>
  );
}
